import { difference, isEqual, sortBy } from "es-toolkit";
import { makeAutoObservable, action, computed, reaction, comparer } from "mobx";
import { nanoid } from "nanoid";

import { getRandomTailwindColor } from "@/utils/tailwindColors";

import type { AuthModel } from "@/models/auth/AuthModel";
import type { TailwindColor } from "@/utils/tailwindColors";
import type { HocuspocusProvider } from "@hocuspocus/provider";

export type UserState = {
  twColor: TailwindColor;
  name?: string;
  username?: string;
  avatarUrl?: string | null;
};

export type PointerPosition = {
  top: number;
  left: number;
  rel: { pos: number; dx: number; dy: number } | null;
};

export type AwarenessState = {
  clientId: string;
  expressionCursor: { id: string; index: number };
  pointer: PointerPosition;
  user?: UserState;
};

export class AwarenessModel {
  clientId?: string;

  user: UserState;

  private disposeUserReaction: () => void;

  constructor(
    readonly provider: HocuspocusProvider,
    private auth: AuthModel,
    private userStateMap = new Map<string, AwarenessState>(),
  ) {
    this.clientId = nanoid();

    this.user = {
      twColor: getRandomTailwindColor(this.clientId),
      name: auth.currentUser?.name,
      username: auth.currentUser?.username,
      avatarUrl: auth.currentUser?.avatarUrls?.["48"],
    };
    this.setUser(this.user);

    this.provider.setAwarenessField("clientId", this.clientId);

    this.disposeUserReaction = reaction(
      () => ({
        name: this.auth.currentUser?.name,
        username: this.auth.currentUser?.username,
        avatarUrl: this.auth.currentUser?.avatarUrls?.["48"],
      }),
      ({ name, username, avatarUrl }) => {
        this.user.name = name;
        this.user.username = username;
        this.user.avatarUrl = avatarUrl;
        this.setUser(this.user);
      },
      { equals: comparer.shallow },
    );

    this.provider.on(
      "awarenessUpdate",
      action(({ states }: { states: AwarenessState[] }) => {
        const clientIds: string[] = [];
        states.forEach((state) => {
          if (state.user?.username !== this.user.username) {
            if (!isEqual(this.userStateMap.get(state.clientId), state)) {
              this.userStateMap.set(state.clientId, state);
            }
            clientIds.push(state.clientId);
          }
        });
        for (const id of difference([...this.userStateMap.keys()], clientIds)) {
          this.userStateMap.delete(id);
        }
      }),
    );

    makeAutoObservable<this, "disposeUserReaction">(this, {
      clientIds: computed({ equals: comparer.structural }),
      userStates: computed({ equals: comparer.structural }),
      disposeUserReaction: false,
    });
  }

  dispose() {
    this.disposeUserReaction();
  }

  cursorState(id: string, index?: number): AwarenessState | undefined {
    return computed(
      () =>
        [...this.userStateMap.values()].find(
          ({ expressionCursor }) =>
            expressionCursor && expressionCursor.id === id && expressionCursor.index === index,
        ),
      { name: "AwarenessModel.cursorState" },
    ).get();
  }

  setUser(user: UserState) {
    this.provider.setAwarenessField("user", user);
  }

  setCursor(id?: string, index?: number) {
    this.provider.setAwarenessField("expressionCursor", { id, index });
  }

  setPointer(pointer: PointerPosition | null) {
    this.provider.setAwarenessField("pointer", pointer);
  }

  get clientIds(): string[] {
    return [...this.userStateMap.keys()];
  }

  get userStates(): UserState[] {
    const userStates = sortBy(
      [...this.userStateMap.values()],
      [(state) => !state.user?.username, (state) => state.user?.username || ""],
    );

    return userStates.map((state) => state.user!).filter(Boolean);
  }

  userState(clientId: string): UserState | undefined {
    return computed(() => this.userStateMap.get(clientId)?.user, {
      name: "AwarenessModel.userState",
      equals: comparer.structural,
    }).get();
  }

  pointer(clientId: string): PointerPosition | undefined {
    return this.userStateMap.get(clientId)?.pointer;
  }
}
